// LayoutGeometry — read-only SVG render of a NormalizedLayoutDto.
//
// Used by every "viewer" surface that previously rendered the raw
// uploaded source image as a backdrop:
//   - FloorExpandedView (single-floor command-center view under the
//     floor stack)
//   - SiteLayoutEditor's per-floor canvas (marker-placement editor)
//
// The editor itself (FloorLayoutEditor) renders the same layers but
// with drag handles + selection, so it has its own copy. Keep this
// component pure: no event handlers, no state, just visuals.
//
// Visual style mirrors the editor — cyan walls, dim semi-transparent
// rooms, yellow doors with swing arc, faint corridor highlight, amber
// entry-point pins, light grey labels — so an operator sees the same
// look across viewer and editor.

import React from "react";
import type { NormalizedLayoutDto } from "../lib/floorStackApi.js";

interface Props {
  layout: NormalizedLayoutDto;
  /**
   * Tone down opacity if you're stacking other markers on top (the
   * floor command view stacks 5 overlay layers above this one). 1 in
   * the layout editor where it's the only thing rendered.
   */
  opacity?: number;
}

export default function LayoutGeometry({ layout, opacity = 1 }: Props): React.ReactElement {
  return (
    <g data-testid="layout-geometry" opacity={opacity}>
      {layout.corridors?.map((c) => (
        <polyline
          key={c.id}
          points={c.centerline.map((p) => `${p.x},${p.y}`).join(" ")}
          fill="none"
          stroke="#00d4ff"
          strokeWidth={c.width_px || 4}
          strokeOpacity={0.18}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
      {layout.rooms?.map((r) => (
        <polygon
          key={r.id}
          points={r.vertices.map((v) => `${v.x},${v.y}`).join(" ")}
          fill="rgba(0, 212, 255, 0.06)"
          stroke="rgba(0, 212, 255, 0.35)"
          strokeWidth={1.5}
        />
      ))}
      {layout.walls?.map((w) => (
        <line
          key={w.id}
          x1={w.points[0].x}
          y1={w.points[0].y}
          x2={w.points[1].x}
          y2={w.points[1].y}
          stroke={w.kind === "exterior" ? "#e5e7eb" : "#9ca3af"}
          strokeWidth={w.thickness_px ?? (w.kind === "exterior" ? 6 : 3)}
          strokeLinecap="round"
        />
      ))}
      {layout.doors?.map((d) => (
        <g key={d.id}>
          <line
            x1={d.hinge.x}
            y1={d.hinge.y}
            x2={d.swing_to.x}
            y2={d.swing_to.y}
            stroke="#fbbf24"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeOpacity={0.85}
          />
          <circle cx={d.hinge.x} cy={d.hinge.y} r={3} fill="#fbbf24" />
        </g>
      ))}
      {layout.entry_points?.map((p) => (
        <g key={p.id}>
          <circle
            cx={p.point.x}
            cy={p.point.y}
            r={9}
            fill="#fbbf24"
            fillOpacity={0.85}
            stroke="#0b0d12"
            strokeWidth={2}
          />
          {p.label && (
            <text
              x={p.point.x + 12}
              y={p.point.y + 4}
              fill="#fbbf24"
              fontSize={11}
              style={{ pointerEvents: "none" }}
            >
              {p.label}
            </text>
          )}
        </g>
      ))}
      {layout.labels?.map((l) => (
        <text
          key={l.id}
          x={l.point.x}
          y={l.point.y}
          fill="rgba(229,231,235,0.9)"
          fontSize={l.font_size_px || 13}
          transform={l.rotation_deg ? `rotate(${l.rotation_deg} ${l.point.x} ${l.point.y})` : undefined}
          style={{ pointerEvents: "none" }}
        >
          {l.text}
        </text>
      ))}
    </g>
  );
}
